/**
 * Generate a unique, stable key for a new form field.
 *
 * field.key is the identity the form-fill UI binds each field's value to
 * (responses[field.key]) AND the shape responses are saved under. It MUST be
 * unique within a form — if two fields share a key, typing in one fills the
 * other (responses[key] is a single shared bucket).
 *
 * The old generator used `field_${allFields.length + 1}`, a positional counter
 * that collides as soon as a field is deleted and another added (length is no
 * longer monotonic). Derive the key from the field's uuid instead, so it can
 * never collide regardless of add/delete order. Hyphens are stripped so the key
 * stays a safe identifier (used as an object key + response column).
 */
export function makeFieldKey(uuid: string): string {
  return `field_${uuid.replace(/-/g, '')}`;
}

/**
 * The key a field's response is stored/bound/submitted under.
 *
 * Normally this is `field.key`. But legacy registration forms (seeded by the
 * now-removed core-registration-fields code) have fields with NO key — their
 * stable identity is `field.id` (e.g. "core_first_name"). Binding those by the
 * missing key makes every such field share responses[undefined] (typing in one
 * fills the others). Fall back to `field.id`, which is always unique, so each
 * field has its own value. Mirrors the backend maps-to-walker.
 */
export function responseKey(field: { key?: string | null; id: string }): string {
  return field.key || field.id;
}
